import React, { useState, useEffect } from 'react';
import {
  Package, Info,
  MapPin, History, ClipboardList, Truck,
  Loader2, AlertCircle, Printer
} from 'lucide-react';
import { ItemsDto } from '../../../../types/items';
import { ItemLocationBalanceDto } from '../../../../types/itemLocationBalance';
import { StcTransactionDto } from '../../../../types/transactionHistory';
import { AdvancedItemConfigDto } from '../../../../types/advancedItemConfig';
import { POrderLineWithHeaderDto } from '../../../../types/purchaseOrderLines';
import { itemLocationBalanceService } from '../../../../services/itemLocationBalanceService';
import { transactionHistoryService } from '../../../../services/transactionHistoryService';
import { itemsService } from '../../../../services/itemsService';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import orderService, { OrderLineByItemDto } from '../../../../services/orderService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { LabelPrintModal, type LabelPrintItem } from '../../../common/LabelPrintModal';

interface ItemDetailsProps {
  item: ItemsDto;
  onBack: () => void;
}

type TabId = 'info' | 'stock' | 'transactions' | 'orders' | 'purchaseOrders';

interface TabDef {
  id: TabId;
  labelKey: string;
  icon: React.ReactNode;
  badge?: number;
  loading?: boolean;
}

// Field display helper
const Field: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{label}</label>
    <p className={`text-sm text-gray-900 ${mono ? 'font-mono' : ''} break-words`}>{value || '-'}</p>
  </div>
);

// Loading / error wrapper for tab content
const TabContent: React.FC<{
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
}> = ({ loading, error, children }) => {
  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-600 py-6 justify-center">
        <AlertCircle className="h-5 w-5 flex-shrink-0" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }
  return <>{children}</>;
};

export const ItemDetails: React.FC<ItemDetailsProps> = ({ item, onBack }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('info');
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Related data state
  const [itemBins, setItemBins] = useState<ItemLocationBalanceDto[]>([]);
  const [itemBinsLoading, setItemBinsLoading] = useState(true);
  const [itemBinsError, setItemBinsError] = useState<string | null>(null);

  const [transactions, setTransactions] = useState<StcTransactionDto[]>([]);
  const [transLoading, setTransLoading] = useState(true);
  const [transError, setTransError] = useState<string | null>(null);

  const [orderLines, setOrderLines] = useState<OrderLineByItemDto[]>([]);
  const [orderLinesLoading, setOrderLinesLoading] = useState(true);
  const [orderLinesError, setOrderLinesError] = useState<string | null>(null);

  const [poLines, setPoLines] = useState<POrderLineWithHeaderDto[]>([]);
  const [poLinesLoading, setPoLinesLoading] = useState(true);
  const [poLinesError, setPoLinesError] = useState<string | null>(null);

  const [advConfig, setAdvConfig] = useState<AdvancedItemConfigDto | null>(null);
  const [advConfigLoading, setAdvConfigLoading] = useState(true);
  const [advConfigError, setAdvConfigError] = useState<string | null>(null);

  useEffect(() => {
    loadItemBins();
    loadTransactions();
    loadOrderLines();
    loadPOLines();
    loadAdvancedConfig();
  }, [item.itemNo]);

  const loadItemBins = async () => {
    try {
      setItemBinsLoading(true);
      setItemBinsError(null);
      const data = await itemLocationBalanceService.getItemBinsByItemNo(item.itemNo);
      setItemBins(data);
    } catch (err) {
      setItemBinsError('Kunne ikke laste lagerbeholdning');
      console.error('Error loading item bins:', err);
    } finally {
      setItemBinsLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setTransLoading(true);
      setTransError(null);
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setMonth(fromDate.getMonth() - 3);
      const data = await transactionHistoryService.getTransactionHistory(
        fromDate, toDate, undefined, item.itemNo
      );
      const sorted = data.sort((a, b) => new Date(b.transDate).getTime() - new Date(a.transDate).getTime()).slice(0, 50);
      setTransactions(sorted);
    } catch (err) {
      setTransError('Kunne ikke laste transaksjonshistorikk');
      console.error('Error loading transactions:', err);
    } finally {
      setTransLoading(false);
    }
  };

  const loadOrderLines = async () => {
    try {
      setOrderLinesLoading(true);
      setOrderLinesError(null);
      const data = await orderService.getOrderLinesByItem(item.itemNo);
      setOrderLines(data);
    } catch (err) {
      setOrderLinesError('Kunne ikke laste ordrelinjer');
      console.error('Error loading order lines:', err);
    } finally {
      setOrderLinesLoading(false);
    }
  };

  const loadPOLines = async () => {
    try {
      setPoLinesLoading(true);
      setPoLinesError(null);
      const data = await pOrderLineService.searchOpenPOLinesByItem(item.itemNo);
      setPoLines(data);
    } catch (err) {
      setPoLinesError('Kunne ikke laste innkjøpsordrer');
      console.error('Error loading PO lines:', err);
    } finally {
      setPoLinesLoading(false);
    }
  };

  const loadAdvancedConfig = async () => {
    try {
      setAdvConfigLoading(true);
      setAdvConfigError(null);
      const data = await itemsService.getAdvancedConfig(item.companyNo, item.itemNo);
      setAdvConfig(data);
    } catch (err) {
      setAdvConfigError('Kunne ikke laste avansert konfigurasjon');
      console.error('Error loading advanced config:', err);
    } finally {
      setAdvConfigLoading(false);
    }
  };

  // Formatters
  const formatNumber = (num: number | undefined | null): string => {
    if (num === undefined || num === null) return '-';
    return num.toLocaleString('no-NO', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const formatCurrency = (amount: number | undefined | null, currency: string | undefined | null): string => {
    if (amount === undefined || amount === null) return '-';
    const cur = (currency && currency !== 'NULL') ? currency : '';
    return `${formatNumber(amount)} ${cur}`.trim();
  };

  const formatDate = (date: string | Date | undefined | null): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('no-NO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatDateTime = (date: string | Date | undefined | null): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('no-NO', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatBoolean = (value: boolean | undefined | null): string => {
    if (value === undefined || value === null) return '-';
    return value ? t('warehouse.modules.itemAdmin.fields.yes') : t('warehouse.modules.itemAdmin.fields.no');
  };

  const getItemTypeLabel = (type: number | undefined | null): string => {
    switch (type) {
      case 0: return 'Standard';
      case 1: return 'Standard';
      case 2: return 'Serienr. (dokument)';
      case 4: return 'Batch m/holdbarhet';
      case 5: return 'Batch (lagerstyrt)';
      case 6: return 'Serienr. (lagerstyrt)';
      default: return type?.toString() || '-';
    }
  };

  const isSerial = item.itemType === 2 || item.itemType === 6;
  const isBatch = item.itemType === 4 || item.itemType === 5;

  // Helper: format code + description
  const codeWithDesc = (code: string | number | undefined | null, desc: string | undefined | null): React.ReactNode => {
    if (!code && code !== 0) return '-';
    if (!desc) return code;
    return <>{code} <span className="text-gray-500">— {desc}</span></>;
  };

  // Tab definitions
  const tabs: TabDef[] = [
    { id: 'info', labelKey: 'warehouse.modules.itemAdmin.tabs.info', icon: <Info className="h-4 w-4" />, loading: advConfigLoading },
    { id: 'stock', labelKey: 'warehouse.modules.itemAdmin.tabs.stock', icon: <MapPin className="h-4 w-4" />, badge: itemBins.length, loading: itemBinsLoading },
    { id: 'transactions', labelKey: 'warehouse.modules.itemAdmin.tabs.transactions', icon: <History className="h-4 w-4" />, badge: transactions.length, loading: transLoading },
    { id: 'orders', labelKey: 'warehouse.modules.itemAdmin.tabs.orders', icon: <ClipboardList className="h-4 w-4" />, badge: orderLines.length, loading: orderLinesLoading },
    { id: 'purchaseOrders', labelKey: 'warehouse.modules.itemAdmin.tabs.purchaseOrders', icon: <Truck className="h-4 w-4" />, badge: poLines.length, loading: poLinesLoading },
  ];

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header: back + title + tabs */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 sm:p-6 pb-0">
          <div className="flex items-center gap-3 mb-3">
            <BackButton onClick={onBack} label={t('warehouse.modules.itemAdmin.backToList')} />
          </div>

          <div className="flex items-start gap-3 sm:gap-4">
            <div className="bg-blue-100 rounded-full p-2 sm:p-3 flex-shrink-0">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{item.itemNo}</h1>
              <p className="text-sm sm:text-base text-gray-600 break-words mt-1">{item.description1 || t('warehouse.modules.itemAdmin.fields.noDescription')}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  {getItemTypeLabel(item.itemType)}
                </span>
                {item.itemBarCode && (
                  <span className="inline-flex items-center text-xs font-mono px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    EAN: {item.itemBarCode}
                  </span>
                )}
              </div>
            </div>

            {/* Print label */}
            <button
              onClick={() => setShowPrintModal(true)}
              className="flex-shrink-0 inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 transition-colors"
              title={t('warehouse.modules.itemAdmin.printLabel')}
            >
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">{t('warehouse.modules.itemAdmin.printLabel')}</span>
            </button>
          </div>
        </div>

        {/* Tab bar directly under header */}
        <div className="border-b border-gray-200 overflow-x-auto overflow-y-hidden mt-4">
          <nav className="flex -mb-px min-w-max" role="tablist">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.icon}
                  <span className="hidden sm:inline">{t(tab.labelKey)}</span>
                  {tab.loading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />
                  ) : tab.badge !== undefined && tab.badge > 0 ? (
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        <div className="p-4 sm:p-5">
          {/* Info tab - basic item information + configuration */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Grunndata */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-1">{t('warehouse.modules.itemAdmin.sections.basicInfo')}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-3">
                  <Field label={t('warehouse.modules.itemAdmin.fields.itemNumber')} value={item.itemNo} mono />
                  <Field label={t('warehouse.modules.itemAdmin.fields.description')} value={item.description1} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.barcode')} value={item.itemBarCode} mono />
                  <Field label={t('warehouse.modules.itemAdmin.fields.supplierItem')} value={item.supplierItemNo} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.itemType')} value={getItemTypeLabel(item.itemType)} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.unitCode')} value={codeWithDesc(item.itemUnitCode, item.itemUnitTxt)} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.unitFactor')} value={formatNumber(item.unitFactor)} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.purchasePrice')} value={formatCurrency(item.purchasePrice, item.currency)} />
                  {item.itemPictureName && <Field label="Bildereferanse" value={item.itemPictureName} />}
                </div>
                {/* Varegrupper */}
                {(item.itemGrp1 || item.itemGrp2 || item.itemGrp3) && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-50">
                    <Field label={t('warehouse.modules.itemAdmin.fields.itemGroup1')} value={item.itemGrp1} />
                    <Field label={t('warehouse.modules.itemAdmin.fields.itemGroup2')} value={item.itemGrp2} />
                    <Field label={t('warehouse.modules.itemAdmin.fields.itemGroup3')} value={item.itemGrp3} />
                  </div>
                )}
              </div>

              {/* Lagerstyring */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-1">{t('warehouse.modules.itemAdmin.details.advancedConfig')}</h3>
                {advConfigLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    <span className="text-sm text-gray-500">Laster konfigurasjon...</span>
                  </div>
                ) : advConfigError ? (
                  <div className="flex items-center gap-2 text-red-600 text-sm py-2">
                    <AlertCircle className="h-4 w-4" />
                    <span>{advConfigError}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-3">
                    <Field label="Lokasjongruppe" value={codeWithDesc(advConfig?.binGrpNo, advConfig?.binGrpDescription)} />
                    <Field label="Plukkregelstrategi" value={codeWithDesc(advConfig?.pickRuleNo, advConfig?.pickRuleName)} />
                    <Field label="Bevegelsestype" value={codeWithDesc(advConfig?.movementTypeNo, advConfig?.movementTypeDescription)} />
                    <Field label="Sorterings-boks" value={codeWithDesc(advConfig?.stcBoxTypeID, advConfig?.stcBoxTypeDescription)} />
                    <Field label="Reolstørrelsesgruppe" value={codeWithDesc(item.itemBinSizeGroupID, advConfig?.itemBinSizeGroupDescription)} />
                    <Field label="Rapport" value={codeWithDesc(item.reportID, advConfig?.reportName)} />
                    <Field label="Std. lager" value={item.stdLocationName} />
                    <Field label="Std. lokasjon" value={advConfig?.stdBinCode || item.stdBinCode} mono />
                    <Field label="Dedikert lokasjon" value={advConfig?.dedicatedBinCode} mono />
                  </div>
                )}

                {/* ItemBinSize table */}
                {advConfig?.itemBinSizes && advConfig.itemBinSizes.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">{t('warehouse.modules.itemAdmin.details.binSizeConfig')}</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 rounded">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reolstørrelse</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Maks antall</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">VP-prioritet</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {advConfig.itemBinSizes.map((bs) => (
                            <tr key={bs.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2">{bs.binSizeDescription || bs.binSizeNo}</td>
                              <td className="px-3 py-2 text-right font-medium">{formatNumber(bs.maxQuantity)}</td>
                              <td className="px-3 py-2 text-right">{bs.putAwayPriorityNo}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Konfigurasjoner */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-1">{t('warehouse.modules.itemAdmin.sections.configuration')}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-3">
                  <Field label={t('warehouse.modules.itemAdmin.fields.stockTransactions')} value={formatBoolean(item.stcTransactionsYesNo)} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.useSharedBin')} value={formatBoolean(item.useSharedBinYesNo)} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.newLine')} value={formatBoolean(item.newLineYesNo)} />
                  <Field label="Vareplassering til plukkbar lokasjon" value={formatBoolean(item.putAwayToPickableBinYesNo)} />
                  {isBatch && <Field label="Tillat blandede batcher" value={formatBoolean(item.allowMixedBatches)} />}
                  <Field label={t('warehouse.modules.itemAdmin.fields.useMaxReplenish')} value={formatBoolean(item.useMaxReplenishQtyYesNo)} />
                  {item.useMaxReplenishQtyYesNo && (
                    <Field label={t('warehouse.modules.itemAdmin.fields.maxReplenishQty')} value={formatNumber(item.maxReplenishQty)} />
                  )}
                </div>
              </div>

              {/* Fritekstfelt */}
              {(item.freeTxt1 || item.freeTxt2 || item.freeTxt3 || item.freeTxt4 || item.freeTxt5) && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-1">{t('warehouse.modules.itemAdmin.sections.freeFields')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {item.freeTxt1 && <Field label={t('warehouse.modules.itemAdmin.fields.freeText1')} value={item.freeTxt1} />}
                    {item.freeTxt2 && <Field label={t('warehouse.modules.itemAdmin.fields.freeText2')} value={item.freeTxt2} />}
                    {item.freeTxt3 && <Field label={t('warehouse.modules.itemAdmin.fields.freeText3')} value={item.freeTxt3} />}
                    {item.freeTxt4 && <Field label={t('warehouse.modules.itemAdmin.fields.freeText4')} value={item.freeTxt4} />}
                    {item.freeTxt5 && <Field label={t('warehouse.modules.itemAdmin.fields.freeText5')} value={item.freeTxt5} />}
                  </div>
                </div>
              )}

              {/* Endringshistorikk */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-1">{t('warehouse.modules.itemAdmin.sections.auditInfo')}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
                  <Field label={t('warehouse.modules.itemAdmin.fields.createdBy')} value={item.createdBy} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.createdDate')} value={formatDateTime(item.created as any)} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.updatedBy')} value={item.updatedBy} />
                  <Field label={t('warehouse.modules.itemAdmin.fields.updatedDate')} value={formatDateTime(item.lastUpdate as any)} />
                </div>
              </div>
            </div>
          )}

          {/* Stock locations tab */}
          {activeTab === 'stock' && (
            <TabContent loading={itemBinsLoading} error={itemBinsError}>
              {/* Quick stats */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <div className="text-xs text-gray-500 mb-1">{t('warehouse.modules.itemAdmin.fields.inventory')}</div>
                  <div className="text-lg font-semibold text-gray-900">{formatNumber(item.quantity)}</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <div className="text-xs text-gray-500 mb-1">{t('warehouse.modules.itemAdmin.fields.available')}</div>
                  <div className="text-lg font-semibold text-green-600">{formatNumber(item.quantityAvailable)}</div>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg text-center">
                  <div className="text-xs text-gray-500 mb-1">{t('warehouse.modules.itemAdmin.fields.reserved')}</div>
                  <div className="text-lg font-semibold text-orange-600">{formatNumber(item.quantityReserved)}</div>
                </div>
              </div>
              {itemBins.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">{t('warehouse.modules.itemAdmin.details.noStockLocations')}</p>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:-mx-5">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lokasjon</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sone</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Antall</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Reservert</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tilgj.</th>
                        {isSerial && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Serienr.</th>}
                        {isBatch && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>}
                        {isBatch && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Utl.dato</th>}
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sist beveg.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {itemBins.map((bin, i) => (
                        <tr key={`${bin.binCode}-${bin.serialNo || bin.batchNo || i}`} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs">{bin.binCode}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{bin.zoneDescription || bin.stcZoneID || '-'}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatNumber(bin.quantity)}</td>
                          <td className="px-3 py-2 text-right text-orange-600">{formatNumber(bin.quantityReserved)}</td>
                          <td className="px-3 py-2 text-right text-green-600">{formatNumber(bin.quantityAvailable)}</td>
                          {isSerial && <td className="px-3 py-2 text-xs font-mono text-purple-700">{bin.serialNo || '-'}</td>}
                          {isBatch && <td className="px-3 py-2 text-xs font-mono text-orange-700">{bin.batchNo || '-'}</td>}
                          {isBatch && <td className="px-3 py-2 text-xs">{formatDate(bin.expiryDate)}</td>}
                          <td className="px-3 py-2 text-xs text-gray-500">{formatDate(bin.lastMovementDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabContent>
          )}

          {/* Transactions tab */}
          {activeTab === 'transactions' && (
            <TabContent loading={transLoading} error={transError}>
              {transactions.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">{t('warehouse.modules.itemAdmin.details.noTransactions')}</p>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:-mx-5">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dato</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lokasjon</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Antall</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ordre</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Operatør</th>
                        {(isSerial || isBatch) && <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Serial/Batch</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDateTime(tx.transDate)}</td>
                          <td className="px-3 py-2">
                            <StatusBadge value={tx.transType} statusType={27} />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{tx.binCode}</td>
                          <td className={`px-3 py-2 text-right font-medium ${tx.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {tx.quantity > 0 ? '+' : ''}{formatNumber(tx.quantity)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">{tx.orderNoERP || (tx.pListID ? `PL-${tx.pListID}` : '-')}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{tx.operatorID || '-'}</td>
                          {(isSerial || isBatch) && (
                            <td className="px-3 py-2 text-xs font-mono">
                              {tx.serialNo && <span className="text-purple-700">{tx.serialNo}</span>}
                              {tx.batchNo && <span className="text-orange-700">{tx.batchNo}</span>}
                              {!tx.serialNo && !tx.batchNo && '-'}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {transactions.length >= 50 && (
                    <p className="text-xs text-gray-400 text-center py-2">{t('warehouse.modules.itemAdmin.details.showingLast50')}</p>
                  )}
                </div>
              )}
            </TabContent>
          )}

          {/* Active orders tab */}
          {activeTab === 'orders' && (
            <TabContent loading={orderLinesLoading} error={orderLinesError}>
              {orderLines.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">{t('warehouse.modules.itemAdmin.details.noActiveOrders')}</p>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:-mx-5">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ordre</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Linje</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Bestilt</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Plukket</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Levert</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Levering</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lager</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {orderLines.map((line) => (
                        <tr key={`${line.orderNo}-${line.lineNo}`} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{line.orderNo}</td>
                          <td className="px-3 py-2 text-gray-600">{line.lineNo}</td>
                          <td className="px-3 py-2 text-right">{formatNumber(line.quantity)}</td>
                          <td className="px-3 py-2 text-right text-blue-600">{formatNumber(line.quantityPicked)}</td>
                          <td className="px-3 py-2 text-right text-green-600">{formatNumber(line.quantityDelivered)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{formatDate(line.deliveryDate)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{line.warehouse || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabContent>
          )}

          {/* Purchase orders tab */}
          {activeTab === 'purchaseOrders' && (
            <TabContent loading={poLinesLoading} error={poLinesError}>
              {poLines.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">{t('warehouse.modules.itemAdmin.details.noOpenPurchaseOrders')}</p>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:-mx-5">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Best.nr</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Leverandør</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Bestilt</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Mottatt</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gjenstår</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lev.dato</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {poLines.map((line) => (
                        <tr key={`${line.orderNo}-${line.lineNo}`} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{line.orderNoERP || line.orderNo}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 truncate max-w-[150px]">{line.supplierName || line.supplierNo}</td>
                          <td className="px-3 py-2 text-right">{formatNumber(line.quantity)}</td>
                          <td className="px-3 py-2 text-right text-green-600">{formatNumber(line.quantityDelivered)}</td>
                          <td className="px-3 py-2 text-right font-medium text-orange-600">{formatNumber(line.remainingQuantity)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{formatDate(line.deliveryDate)}</td>
                          <td className="px-3 py-2">
                            <StatusBadge value={line.lineStatus} statusType={1} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabContent>
          )}

        </div>
      </div>

      {/* Print label modal (shared component with built-in printer selection) */}
      <LabelPrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        printType="item"
        items={[{
          id: item.itemNo,
          displayName: `${item.itemNo} - ${item.description1 || ''}`.trim(),
          itemNo: item.itemNo,
          itemDescription: item.description1 || undefined,
        } as LabelPrintItem]}
      />
    </div>
  );
};
